import { useCallback, useState } from 'react'
import { OccasionalSlotsPlaces } from '@/types/Slots.ts'
import { ChildWithParents } from '@/types/Child.ts'
import { StaffWithLinks } from '@/types/Staff.ts'
import { Parent } from '@/types/Parent.ts'
import { useLazyQuery } from '@apollo/client'
import { GET_PLACES } from '@/modules/API/daycare/daycareHandler.ts'
import { GET_CHILD_LIST, GET_PARENT } from '@/modules/API/parent/parentHandler.ts'
import { GET_STAFF_LIST } from '@/modules/API/staff/staffHandler.ts'
import { Role } from '@/types/Role.ts'
import { ContextUser } from '@/types/Context.ts'

export function usePlanningData(user: ContextUser | null) {
    const [lastDaycareFetch, setLastDaycareFetch] = useState<number | undefined>(undefined)

    const [getDaycarePlaces, { data: dataPlaces }] = useLazyQuery(GET_PLACES)
    const [getChildList, { data: dataChilds }] = useLazyQuery(GET_CHILD_LIST)
    const [getParent, { data: dataParent }] = useLazyQuery(GET_PARENT)
    const [getStaffList, { data: dataStaffs }] = useLazyQuery(GET_STAFF_LIST)

    const places: OccasionalSlotsPlaces[] = dataPlaces?.getOccasionalSlotsPlaces ?? []
    const childs: ChildWithParents[] = dataChilds?.getChildList ?? []
    const parent: Parent | undefined = dataParent?.getParent
    const staffs: StaffWithLinks[] = dataStaffs?.getStaffList ?? []

    const refetchAll: () => Promise<void> = useCallback(async (): Promise<void> => {
        if (!user?.currentDaycare || lastDaycareFetch === user.currentDaycare.id) return

        if (user.role === Role.parent) await getParent()
        else {
            await Promise.all([
                user.currentDaycareRights.canReadChild
                    ? getChildList({ variables: { daycareId: user.currentDaycare.id } })
                    : '',
                getStaffList({ variables: { daycareId: user.currentDaycare.id } })
            ])
        }
        if (user.currentDaycareRights.canReadOccasionalSlot) {
            await getDaycarePlaces({ variables: { daycareId: user.currentDaycare.id } })
        }

        setLastDaycareFetch(user.currentDaycare?.id)
    }, [user, lastDaycareFetch, getParent, getChildList, getStaffList, getDaycarePlaces])

    return {
        parent,
        childs,
        places,
        staffs,
        refetchAll
    }
}
